import { classifyScannedCode, parseSellerItemRef } from '@barso/shared';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent,
  type SyntheticEvent,
} from 'react';
import { flushSync } from 'react-dom';
import { Keypad } from '../components/Keypad';
import { Button } from '../components/ui';
import { euro } from '../format';
import { displayCode } from '../pos/cart';
import type { CatalogIndex, IndexEntry } from '../pos/catalogIndex';
import { searchCatalog, sellable, type SearchResult } from '../pos/catalogSearch';
import { pushCodeDigits, removeLastCodeDigit } from '../pos/keypad';
import { Sheet } from './posParts';

/** Zuletzt gewählte Eingabeart der Suche, je Gerät gemerkt: `pad` (123) oder `text` (ABC). */
export const SEARCH_MODE_KEY = 'barso.pos.searchMode';

function storedMode(): 'pad' | 'text' | null {
  try {
    const value = localStorage.getItem(SEARCH_MODE_KEY);
    return value === 'pad' || value === 'text' ? value : null;
  } catch {
    return null;
  }
}

function rememberMode(mode: 'pad' | 'text'): void {
  try {
    localStorage.setItem(SEARCH_MODE_KEY, mode);
  } catch {
    // Ohne Speicher gilt die Wahl nur für dieses Öffnen
  }
}

/** Verzögerung nach der letzten Taste, bevor gesucht wird. */
export const SEARCH_DELAY_MS = 120;

const STATUS_TEXT: Record<string, string> = {
  sold: 'verkauft',
  listed: 'nicht angenommen',
  missing: 'fehlt',
  returned: 'zurückgegeben',
};

/** Hinweis am Treffer, wenn der Artikel nicht ohne Weiteres verkäuflich ist. */
export function statusNote(entry: IndexEntry): string | null {
  if (entry.deleted) return 'gelöscht';
  if (entry.status === 'received') return null;
  return STATUS_TEXT[entry.status] ?? entry.status;
}

/** Verzögerter Wert (die Suche läuft erst, wenn die Eingabe kurz ruht). */
function useDelayed(value: string, ms: number): string {
  const [delayed, setDelayed] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayed(value);
    }, ms);
    return () => {
      clearTimeout(timer);
    };
  }, [value, ms]);
  return delayed;
}

/** Suche im Index, neu bei jeder Änderung des Index und der (verzögerten) Eingabe. */
export function useCatalogSearch(index: CatalogIndex, query: string): SearchResult {
  const stamp = useSyncExternalStore(
    (listener) => index.subscribe(listener),
    () => index.stamp,
  );
  const delayed = useDelayed(query, SEARCH_DELAY_MS);
  // `stamp` erneuert die Treffer, wenn der Index sich ändert (Seite des Abzugs, Statuswechsel)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `stamp` ist nur der Auslöser
  const result = useMemo(() => searchCatalog(index.entries(), delayed), [index, delayed, stamp]);
  return result;
}

/**
 * Soll Enter erfassen? Ein vollständiger Code, eine Nummer „17/5“ oder ein Code ohne Treffer ja (wie von Hand oder mit
 * Handscanner); ein Suchwort mit Treffern nein: Dort tippt man auf einen Treffer.
 */
export function enterCaptures(index: CatalogIndex, raw: string): boolean {
  const query = raw.trim();
  if (query === '') return false;
  if (parseSellerItemRef(query) !== null) return true;
  if (classifyScannedCode(query) === null) return false;
  const result = searchCatalog(index.entries(), query);
  return result.kind === 'code' || result.total === 0;
}

/** Trefferliste mit „In den Warenkorb“, gemeinsam für das Blatt und das Suchfeld ohne Kamera. */
export function SearchResults({
  result,
  inCart,
  onAdd,
  onNoLabel,
  live = true,
  compact = false,
}: {
  result: SearchResult;
  inCart: (entry: IndexEntry) => boolean;
  onAdd: (entry: IndexEntry) => void;
  /** Ausweg ohne Treffer: „Artikel ohne Etikett erfassen“ (U17h). */
  onNoLabel?: (() => void) | undefined;
  /** `false`: Die Zahl der Treffer sagt eine dauerhafte Live-Region des Aufrufers an (keine doppelte Ansage). */
  live?: boolean;
  /** Kompakte Zeilen (Blatt mit Ziffernblock): Titel einzeilig, kurzer Knopf, `aria-label` bleibt voll. */
  compact?: boolean;
}) {
  return (
    <>
      <p className="muted" role={live ? 'status' : undefined}>
        {result.total === 0 ? 'Kein Treffer.' : `${String(result.total)} Treffer`}
      </p>
      {result.total === 0 && onNoLabel !== undefined && (
        <Button variant="secondary" onClick={onNoLabel}>
          Artikel ohne Etikett erfassen
        </Button>
      )}
      {result.hits.length > 0 && (
        <ul className="list search-hits">
          {result.hits.map((entry) => {
            const note = statusNote(entry);
            const there = inCart(entry);
            return (
              <li
                key={entry.id}
                className={`search-hit${sellable(entry) ? '' : ' search-hit--dim'}${compact ? ' search-hit--compact' : ''}`}
              >
                <div className="stack search-hit__main">
                  <div className="search-hit__row">
                    <strong>{entry.title}</strong>
                    <strong>{euro(entry.priceCents)}</strong>
                  </div>
                  <span className="muted">
                    {`V${String(entry.sellerNumber)}${entry.itemNo === null ? '' : ` · Nr. ${String(entry.itemNo)}`}${entry.size === null ? '' : ` · Gr. ${entry.size}`}`}
                  </span>
                  <span className="muted search-hit__code">
                    {displayCode(entry.code)}
                    {entry.altCode === null ? '' : ` · ${entry.altCode}`}
                    {note === null ? '' : ` · ${note}`}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  disabled={there}
                  aria-label={
                    there ? `${entry.title} liegt im Warenkorb` : `${entry.title} in den Warenkorb`
                  }
                  onClick={() => {
                    onAdd(entry);
                  }}
                >
                  {compact
                    ? there
                      ? 'Im Korb'
                      : 'Dazu'
                    : there
                      ? 'Im Warenkorb'
                      : 'In den Warenkorb'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {result.more > 0 && (
        <p className="muted">{`${String(result.more)} weitere: bitte genauer suchen.`}</p>
      )}
    </>
  );
}

/**
 * Blatt „Suchen“ (U17b): Suche im Artikelstand dieses Geräts, auch ohne Netz. Ersetzt „Code eintippen“ und „Nummer
 * eingeben“. Findet keine Verkäufernamen (der Stand enthält keine). Mit Enter wird wie von Hand erfasst; „In den
 * Warenkorb“ geht über denselben Weg wie ein Code von Hand (`onAdd`), also mit allen Prüfungen und Rückfragen.
 */
export function SearchSheet({
  index,
  standText,
  inCart,
  onAdd,
  onCapture,
  onNoLabel,
  onClose,
}: {
  index: CatalogIndex;
  /** „im Stand von 10:42“ (Serverzeit des letzten Abzugs), sofern bekannt. */
  standText?: string | undefined;
  inCart: (entry: IndexEntry) => boolean;
  onAdd: (entry: IndexEntry) => void;
  onCapture: (raw: string) => void;
  /** Ausweg ohne Treffer: „Artikel ohne Etikett erfassen“ (U17h). */
  onNoLabel?: (() => void) | undefined;
  onClose: () => void;
}) {
  const coarse =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const [query, setQuery] = useState('');
  // Zuletzt gewählte Eingabeart; ohne Wahl am Handy zuerst der Ziffernblock (keine Systemtastatur), mit Maus die Tastatur
  const [pad, setPadState] = useState(() => (storedMode() ?? (coarse ? 'pad' : 'text')) === 'pad');
  const input = useRef<HTMLInputElement>(null);
  const result = useCatalogSearch(index, query);
  const loading = !index.loaded;
  const searching = !loading && query.trim() !== '';

  // Beim Öffnen: Fokus ins Feld, außer am Handy mit Ziffernblock (dort soll keine Tastatur aufgehen). Der Effekt läuft nach
  // dem des Blatts (das den Fokus auf sich setzt); `autoFocus` würde von ihm überschrieben.
  useEffect(() => {
    if (!pad || !coarse) input.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur einmal beim Öffnen, nicht bei jedem Umschalten
  }, []);

  function choosePad() {
    setPadState(true);
    rememberMode('pad');
  }

  function chooseText() {
    // Erst umschalten (das Feld ist dann beschreibbar, `inputMode` „text“), dann neu fokussieren: so geht die Tastatur auf
    flushSync(() => {
      setPadState(false);
    });
    rememberMode('text');
    input.current?.blur();
    input.current?.focus();
  }

  // Im Ziffernblock-Modus ist das Feld schreibgeschützt (Bildschirmtastatur bleibt zu); Hardware-Tastatur und Handscanner
  // gehen trotzdem über die Tasten. Ein Buchstabe schaltet auf „ABC“ (ohne die Wahl zu merken).
  function padKey(event: KeyboardEvent<HTMLInputElement>) {
    if (!pad || event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
      if (pad && event.key === 'Backspace') {
        event.preventDefault();
        setQuery(removeLastCodeDigit);
      }
      return;
    }
    const key = event.key;
    if (/^\d$/.test(key)) {
      event.preventDefault();
      setQuery((current) => pushCodeDigits(current, key, 30));
    } else if (key === '/') {
      event.preventDefault();
      setQuery((current) => (current === '' ? current : `${current}/`));
    } else if (/\p{L}/u.test(key)) {
      event.preventDefault();
      flushSync(() => {
        setPadState(false);
      });
      setQuery((current) => current + key);
    }
  }

  function submit(event: SyntheticEvent) {
    event.preventDefault();
    if (enterCaptures(index, query)) onCapture(query);
  }

  const liveText = !searching
    ? ''
    : result.total === 0
      ? 'Kein Treffer.'
      : `${String(result.total)} Treffer`;
  const results = searching && (
    <div className={pad ? 'search-results search-results--pad' : 'search-results'}>
      {standText !== undefined && <p className="muted search-stand">{standText}</p>}
      <SearchResults
        result={result}
        inCart={inCart}
        onAdd={onAdd}
        onNoLabel={onNoLabel}
        live={false}
        compact={pad}
      />
    </div>
  );

  return (
    <Sheet title="Suchen" onClose={onClose}>
      {/* Dauerhafte Live-Region: sagt die Zahl der Treffer an (leer ohne Suche) */}
      <p className="sr-only" role="status">
        {liveText}
      </p>
      <form className="stack" onSubmit={submit} noValidate>
        <div className="field search-field">
          <label htmlFor="pos-search">Code, Nummer (17/5) oder Suchbegriff</label>
          <input
            id="pos-search"
            type="search"
            autoComplete="off"
            spellCheck={false}
            maxLength={60}
            enterKeyHint="search"
            // Mit Ziffernblock keine Systemtastatur
            inputMode={pad ? 'none' : 'text'}
            readOnly={pad}
            ref={input}
            value={query}
            onKeyDown={padKey}
            onChange={(change) => {
              setQuery(change.target.value);
            }}
          />
        </div>
        <div className="row" role="group" aria-label="Eingabeart">
          <Button
            variant="secondary"
            small
            aria-pressed={pad}
            className={pad ? 'search-mode search-mode--on' : 'search-mode'}
            onClick={choosePad}
          >
            123
          </Button>
          <Button
            variant="secondary"
            small
            aria-pressed={!pad}
            className={pad ? 'search-mode' : 'search-mode search-mode--on'}
            onClick={chooseText}
          >
            ABC
          </Button>
        </div>
        {loading && <p role="status">Artikelstand wird geladen …</p>}
        {pad ? (
          <div className="search-pad-layout">
            {results}
            <div className="stack search-pad">
              <Keypad
                doubleZero={false}
                onDigits={(digits) => {
                  setQuery((current) => pushCodeDigits(current, digits, 30));
                }}
                onBackspace={() => {
                  setQuery(removeLastCodeDigit);
                }}
              />
              <div className="row search-pad__row">
                <Button
                  variant="secondary"
                  aria-label="Schrägstrich zwischen Verkäufer- und Artikelnummer"
                  onClick={() => {
                    setQuery((current) => (current === '' ? current : `${current}/`));
                  }}
                >
                  /
                </Button>
                <Button type="submit" disabled={query.trim() === ''}>
                  Erfassen
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {results}
            <Button type="submit" disabled={query.trim() === ''}>
              Erfassen
            </Button>
          </>
        )}
      </form>
    </Sheet>
  );
}
